/**
 * <SessionRow> — one upcoming live, as a row of a card-list (molecule).
 *
 * **La carte de la prochaine séance en plus compact** (`<NextSessionHero>`) : un même objet porte
 * les mêmes repères (Wajih, 30/09). La capsule du tuteur à gauche, puis :
 *  1. la ligne horaire, en gras — « Vendredi 2 octobre · 13h35 », ou l'heure seule sous un
 *     libellé de jour (`timeOnly`, « Tous mes lives ») ;
 *  2. la ligne de la séance (`<SessionDetailsLine>`) — la pile de ses matières, puis « Maths ·
 *     Physique-Chimie · avec Wajih · 1h » à l'encre secondaire, sur deux lignes au plus ;
 *  3. ses badges, sous les lignes, comme sur la carte — un seul d'état, au plus un de contexte
 *     (`useSessionBadges`). La ligne ne redit pas « Aujourd'hui » ni son décompte : le jour est
 *     dans sa ligne horaire, ou dans le libellé au-dessus. « En cours », lui, s'y lit.
 * Rien n'est calé à droite, à aucune largeur : la ligne n'a qu'une forme.
 *
 * Multi-tuteur est le cas normal, donc le tuteur est sur CHAQUE ligne : rien ici ne suppose qu'il
 * n'y a qu'un tuteur, ou qu'une séance.
 *
 * **Elle ne dessine pas de carte** : trois séances font UNE carte-liste à séparateurs, pas trois
 * cadres. Le retrait vient de `<CardListRow>`, pour que toutes les lignes du bloc s'alignent.
 *
 * **Pas de menu « ⋮ »** : la ligne entière presse et ouvre les actions de SA séance
 * (`<LiveSessionActionsSheet>`). Deux cibles dans une même ligne — la ligne et son menu — c'est
 * exactement ce qu'on vient de retirer de l'espace parent.
 *
 * **Une séance où l'absence est signalée, ou terminée, est grisée** (`isSessionDimmed`) : la
 * ligne horaire passe à l'encre secondaire (`text-muted`, AA sur la carte
 * dans les deux thèmes), la capsule et la pile de matières s'estompent, et seul son badge —
 * « Absence signalée », « Terminé » — garde sa couleur : c'est lui qui dit pourquoi. La ligne reste
 * touchable : c'est d'elle que part « Annuler l'absence ».
 *
 * **`relativeDay`** écrit le jour comme on le dit — « Aujourd'hui · 18h00 », « Demain · 18h00 » —
 * pour une liste qui se lit seule : sans carte de la prochaine séance au-dessus d'elle ni libellé
 * de jour, c'est la ligne qui doit dire que c'est aujourd'hui. Défaut `false`.
 *
 * **`showRecoveryInfo`** pose, sous les badges, la ligne « Rattrapage de l'absence du … » que la
 * carte de la prochaine séance écrit déjà (`<NextSessionHero>`). Défaut `false` : les listes du
 * parent ne la montrent pas. L'élève, lui, la lit aujourd'hui sur chaque carte de séance, et la
 * garde.
 *
 * **L'espace masteur pose la même ligne**, avec deux places à lui : rien à gauche (`lead={null}` :
 * la capsule serait la sienne), et, à la place de la ligne de la séance (`details`), la sienne — le
 * type de cours et l'école, puis ses élèves un par un, leur classe et leurs matières — comme sur sa
 * carte en grand (`<NextSessionHero>`). Sans ces props, la ligne du parent et de l'élève, inchangée.
 *
 * Composes DS pieces only — `<Avatar>`, `<Text>`, `<Row>`, `<Stack>`, `<SessionDetailsLine>`,
 * `<LiveStatusBadges>`.
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Avatar } from "../../atoms/Avatar"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { useSessionBadges } from "../../hooks/useSessionBadges"
import { CardListRow } from "../CardList"
import { LiveStatusBadges } from "../LiveStatusBadges"
import { SessionDetailsLine } from "../SessionDetailsLine"
import { type UILiveSession } from "../../../ports/primary/ui/types"
import { formatDayTime, formatHour } from "../../../ports/primary/ui/formatters/dateFormatter"
import { isSessionDimmed, tutorShortName } from "../../../ports/primary/ui/formatters/liveSessionFormatter"

export type SessionRowProps = {
    session: UILiveSession
    /** Makes the whole row a button (session picker, or "voir la séance"). */
    onPress?: () => void
    /** Selected state of a picker row — fond lavande. */
    selected?: boolean
    /** Sous un libellé de jour, la ligne ne redit pas la date : elle ne donne que l'heure. */
    timeOnly?: boolean
    /** « Aujourd'hui · 18h00 », « Demain · 18h00 » au lieu de la date. Sans effet sous `timeOnly`. Défaut `false`. */
    relativeDay?: boolean
    /** Écrit `session.recoveryInfo` sous les badges — « Rattrapage de l'absence du … ». Défaut `false`. */
    showRecoveryInfo?: boolean
    /**
     * Ce qui se pose à gauche à la place de la capsule du tuteur. `null` : rien (le masteur) — et pas
     * de place gardée pour le nœud, qui n'est pas là.
     */
    lead?: React.ReactNode
    /**
     * Ce qui se pose sous la ligne horaire à la place de la ligne de la séance
     * (`<SessionDetailsLine>`) : celle du masteur, qui nomme ses élèves, et s'estompe d'elle-même avec
     * la séance (`isSessionDimmed`). Absent : la ligne de toute séance.
     */
    details?: React.ReactNode
    testID?: string
}

/** Le visage signé du tuteur. */
export const AVATAR_SIZE = 34

/** La place du nœud de la capsule, qui pend sous l'avatar. */
export const BOW_ROOM = 6

export function SessionRow(props: SessionRowProps) {
    const {
        session,
        onPress,
        selected = false,
        timeOnly = false,
        relativeDay = false,
        showRecoveryInfo = false,
        lead,
        details,
        testID,
    } = props
    const { date, tutor, recoveryInfo } = session

    const dimmed = isSessionDimmed(session)
    const { badges } = useSessionBadges(session, { today: false })

    const content = (
        <CardListRow style={selected ? styles.selected : undefined}>
            <Row gap="3" align="center" style={lead === null ? undefined : styles.row}>
                {lead !== undefined ? (
                    lead
                ) : (
                    <Avatar
                        uri={tutor.avatarUrl}
                        name={tutorShortName(tutor)}
                        size={AVATAR_SIZE}
                        signed
                        dimmed={dimmed}
                    />
                )}

                <Stack gap="1" style={styles.body}>
                    <Text size="text-md" weight="bold" color={dimmed ? "text-muted" : "text-primary"} numberOfLines={1}>
                        {timeOnly ? formatHour(date) : formatDayTime(date, relativeDay)}
                    </Text>
                    {details !== undefined ? details : <SessionDetailsLine session={session} dimmed={dimmed} />}
                    {badges != null && (
                        <Row gap="2" align="center" style={styles.badges}>
                            <LiveStatusBadges badges={badges} />
                        </Row>
                    )}
                    {showRecoveryInfo && recoveryInfo != null && recoveryInfo !== "" && (
                        <Text size="text-xs" color="text-muted">
                            {recoveryInfo}
                        </Text>
                    )}
                </Stack>
            </Row>
        </CardListRow>
    )

    if (onPress == null) return <View testID={testID}>{content}</View>

    return (
        <Pressable testID={testID} onPress={onPress} accessibilityRole="button">
            {content}
        </Pressable>
    )
}

const styles = StyleSheet.create((theme) => ({
    row: {
        paddingBottom: BOW_ROOM,
    },
    body: {
        flex: 1,
        flexShrink: 1,
    },
    badges: {
        flexWrap: "wrap",
    },
    selected: {
        backgroundColor: theme.colors["primary-soft"],
    },
}))
